/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, July 2020
 */

import React, { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { getContrast } from 'polished';
import {
  FlowBlockPage,
  FlowBlockTerminalPage,
  FlowBlockWarningPage,
} from '@staffino/model';
import {
  Box,
  Paragraph,
  styled,
  Theme,
  useTheme,
} from '@staffino/primitive-controls';
import { ScrollContainer } from '@staffino/flow-controls';
import BlockFactory from './blocks/block-factory';
import TosBlock from './blocks/tos-block';
import useObjects from './use-objects-hook';
import { LocalePicker } from './controls/locale-picker';
import useBridge from './use-bridge-hook';
import usePageStateManager from './use-page-state-manager-hook';

const coverImageUrl = '';
function LegacyLayout({ page, scrollMode }: Props) {
  const [coverImage] = useObjects('cover-image');
  const backgroundImage = coverImage?.imageUrl
    ? `url('${coverImage?.imageUrl}')`
    : undefined;
  return (
    <div className="flow-layout-legacy">
      <div className="left">
        <div className="cover-image" style={{ backgroundImage }}>
          <img alt="" src={coverImageUrl} />
        </div>
      </div>
      <ScrollContainer className="content" scrollMode={scrollMode}>
        <Box p={['2.75rem 0', '2.75rem 1.5rem']} height="100%">
          <BlockFactory block={page} />
        </Box>
      </ScrollContainer>
    </div>
  );
}

function SingleColumnLayout({ page, scrollMode }: Props) {
  const [coverImage] = useObjects('cover-image');
  const backgroundImage = coverImage?.imageUrl
    ? `url('${coverImage?.imageUrl}')`
    : undefined;

  return (
    <div className="flow-layout-legacy">
      <div className="left">
        <div className="cover-image" style={{ backgroundImage }}>
          <img alt="" src={coverImageUrl} />
        </div>
      </div>
      <ScrollContainer className="content" scrollMode={scrollMode}>
        <Box height="100%">
          <BlockFactory block={page} />
        </Box>
      </ScrollContainer>
    </div>
  );
}

type CenteredLayoutRootProps = {
  borderColor?: string;
  backgroundColor?: string;
  backgroundImage?: string;
  theme?: Theme;
};

const CenteredLayoutRoot = styled.div<CenteredLayoutRootProps>((props) => ({
  borderTop: `5px solid ${props.borderColor || props.theme.colors.primary}`,
  ...(!props.backgroundImage
    ? { backgroundColor: props.backgroundColor || '#f7f9fa' }
    : {}),
  ...(props.backgroundImage ? { backgroundImage: props.backgroundImage } : {}),
}));

function calculateReadableColor(
  color: string,
  theme: Theme,
  ...colors: string[]
) {
  const scores = colors.map((c) => ({
    color: c,
    score: getContrast(theme.colors[color] ?? color, theme.colors[c] ?? c),
  }));
  scores.sort((a, b) => b.score - a.score);
  return scores[0].color;
}

function CenteredLayoutFooter({ color }) {
  return (
    <footer>
      <img
        src={`/public/images/staffino-${color.toLowerCase()}.svg`}
        width="62"
        height="14"
        alt="Staffino"
      />
      <Paragraph color={color}>
        <Trans>Independent feedback system</Trans>
      </Paragraph>
      <TosBlock block={{ type: 'tos', id: '1' }} color={color} />
    </footer>
  );
}

function CenteredLayout({ page, scrollMode }: Props) {
  const bridge = useBridge();
  const pageStateManager = usePageStateManager();
  const { t } = useTranslation();
  const locales = useObjects('locale');
  const activeLocale = locales && locales.find((l) => l.id === bridge.localeId);
  const sortedLocales = useMemo(() => {
    const cloned = [...(locales ?? [])];
    cloned.sort((a, b) => a.name.localeCompare(b.name));
    return cloned;
  }, [locales]);

  const [backgroundImage] = useObjects('background-image');
  const backgroundImageUrl = backgroundImage?.imageUrl
    ? `url('${backgroundImage?.imageUrl}')`
    : undefined;

  const theme = useTheme();
  const backgroundColor = page?.backgroundColor || '#f7f9fa';
  const borderColor = page?.borderColor;
  const footerColor = useMemo(
    () => calculateReadableColor(backgroundColor, theme, 'lightText', 'white'),
    [backgroundColor, theme],
  );

  const handleSelect = useCallback(
    async (locale) => {
      try {
        return await bridge.setLocale(locale.id, locale.code);
      } catch (error) {
        pageStateManager.addNotification(
          'info',
          t('Connection lost. Could not perform this action.'),
          5,
        );
        return null;
      }
    },
    [bridge, pageStateManager, t],
  );
  return (
    <CenteredLayoutRoot
      className="flow-layout-centered"
      borderColor={borderColor}
      backgroundColor={backgroundColor}
      backgroundImage={backgroundImageUrl}
    >
      {activeLocale && locales?.length > 1 && (
        <Box style={{ float: 'right' }}>
          <LocalePicker
            activeLocale={activeLocale}
            locales={sortedLocales}
            onSelect={handleSelect}
          />
        </Box>
      )}
      <ScrollContainer className="wrapper" scrollMode={scrollMode}>
        <div className="content">
          <BlockFactory block={page} />
          {backgroundImageUrl && <CenteredLayoutFooter color={footerColor} />}
        </div>
        {!backgroundImageUrl && <CenteredLayoutFooter color={footerColor} />}
      </ScrollContainer>
    </CenteredLayoutRoot>
  );
}

const layouts = {
  legacy: LegacyLayout,
  'single-column': SingleColumnLayout, // TODO: used by Jysk, remove with legacy
  centered: CenteredLayout,
};

type Props = {
  page: FlowBlockPage | FlowBlockTerminalPage | FlowBlockWarningPage;
  layout?: string;
  scrollMode?: 'auto' | 'disabled';
};

function FlowPageLayout({
  page,
  layout = 'legacy',
  scrollMode = 'auto',
}: Props) {
  const Layout = layouts[layout] ?? layouts.legacy;
  return <Layout page={page} scrollMode={scrollMode} />;
}

export default FlowPageLayout;
